// Shared Checkbox — styled to match the app instead of the browser default:
// 2px border, 6px radius, brand colour when ticked with the button-text
// colour for the tick (the same pairing as a primary button). DEV-51010.
//
// The native input stays in the DOM (visually hidden) so the label click,
// Space key and screen readers all work; the box beside it mirrors its
// state through `peer-*`. Hiding it also keeps @tailwindcss/forms' own
// checkbox styling out of the way. Wrap it and its text in a <label> for
// a large touch target.
import { forwardRef } from 'react'
import { Check } from 'lucide-react'
import clsx from 'clsx'

export type CheckboxProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'>

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, ...props }, ref) => (
    <span className={clsx('relative inline-flex h-6 w-6 flex-shrink-0', className)}>
      <input ref={ref} type="checkbox" className="peer sr-only" {...props} />
      <span
        aria-hidden="true"
        className={clsx(
          'pointer-events-none flex h-6 w-6 items-center justify-center rounded-[6px] border-2 border-gray-300 bg-white text-transparent transition-colors',
          'peer-checked:border-brand-primary peer-checked:bg-brand-primary peer-checked:text-[var(--brand-primary-text)]',
          'peer-focus-visible:ring-2 peer-focus-visible:ring-brand-primary peer-focus-visible:ring-offset-2',
          'peer-disabled:opacity-50',
        )}
      >
        <Check className="h-4 w-4" strokeWidth={3} />
      </span>
    </span>
  ),
)

Checkbox.displayName = 'Checkbox'
